import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { Checkbox } from '../../../components/ui/Checkbox';
import { consentService } from '../../../services/consentService';

const ConsentWorkflow = ({ treatmentPlan, onAcknowledgmentChange }) => {
  const [updating, setUpdating] = useState(false);

  const handleAcknowledgmentToggle = async (acknowledgmentId, currentValue) => {
    try {
      setUpdating(true);
      await consentService?.updateAcknowledgment(acknowledgmentId, !currentValue);
      onAcknowledgmentChange();
    } catch (err) {
      alert(err?.message || 'Failed to update acknowledgment');
    } finally {
      setUpdating(false);
    }
  };

  const acknowledgments = treatmentPlan?.consentWorkflow?.acknowledgments || [];
  const acknowledgedCount = acknowledgments?.filter(ack => ack?.acknowledged)?.length;
  const totalCount = acknowledgments?.length;
  const progressPercentage = totalCount > 0 ? (acknowledgedCount / totalCount) * 100 : 0;

  return (
    <div className="space-y-6">
      {/* Progress Indicator */}
      <div className="bg-background rounded-lg border border-border p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-foreground">Consent Progress</span>
          <span className="text-sm text-muted-foreground">{acknowledgedCount} of {totalCount} completed</span>
        </div>
        <div className="w-full bg-muted rounded-full h-2">
          <div 
            className="bg-primary h-2 rounded-full transition-all duration-300"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
      </div>
      {/* Consent Instructions */}
      <div className="bg-primary/5 rounded-lg border border-primary/20 p-4">
        <div className="flex items-start gap-3">
          <Icon name="AlertCircle" size={20} className="text-primary flex-shrink-0 mt-1" />
          <div>
            <h4 className="font-semibold text-foreground mb-2">Before You Proceed</h4>
            <p className="text-sm text-muted-foreground mb-3">
              Please carefully review each treatment phase below. You must acknowledge that you understand the risks, benefits, and alternatives for each phase before providing your digital consent.
            </p>
            <ul className="space-y-1 text-sm text-muted-foreground">
              <li className="flex items-start gap-2">
                <Icon name="CheckCircle" size={16} className="text-primary flex-shrink-0 mt-0.5" />
                <span>Read each acknowledgment statement carefully</span>
              </li>
              <li className="flex items-start gap-2">
                <Icon name="CheckCircle" size={16} className="text-primary flex-shrink-0 mt-0.5" />
                <span>Check the box to confirm your understanding</span>
              </li>
              <li className="flex items-start gap-2">
                <Icon name="CheckCircle" size={16} className="text-primary flex-shrink-0 mt-0.5" />
                <span>Ask questions using the Discussion tab if anything is unclear</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
      {/* Acknowledgment Items */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Icon name="FileCheck" size={20} className="text-primary" />
          Required Acknowledgments
        </h3>
        
        {acknowledgments?.map((ack, idx) => {
          const phase = treatmentPlan?.phases?.find(p => p?.id === ack?.phaseId);
          
          return (
            <div 
              key={ack?.id} 
              className={`bg-background rounded-lg border p-4 transition-all ${
                ack?.acknowledged 
                  ? 'border-success bg-success/5' :'border-border hover:border-primary/50'
              }`}
            >
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-1">
                  <Checkbox
                    checked={ack?.acknowledged}
                    onChange={() => handleAcknowledgmentToggle(ack?.id, ack?.acknowledged)}
                    disabled={updating}
                  />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-semibold">
                      {idx + 1}
                    </span>
                    <h4 className="font-semibold text-foreground">{phase?.phaseName}</h4>
                    {ack?.acknowledged && (
                      <Icon name="CheckCircle" size={18} className="text-success" />
                    )}
                  </div>
                  <p className="text-sm text-foreground leading-relaxed mb-2">
                    {ack?.acknowledgmentText}
                  </p>
                  {ack?.acknowledged && ack?.acknowledgedAt && (
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <Icon name="Clock" size={12} />
                      Acknowledged on {new Date(ack.acknowledgedAt)?.toLocaleString()}
                    </p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {/* Risks and Benefits Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-background rounded-lg border border-border p-4">
          <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2">
            <Icon name="ThumbsUp" size={18} className="text-success" />
            Benefits of Treatment
          </h4>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <Icon name="Check" size={16} className="text-success flex-shrink-0 mt-0.5" />
              <span>Restoration of optimal oral health and function</span>
            </li>
            <li className="flex items-start gap-2">
              <Icon name="Check" size={16} className="text-success flex-shrink-0 mt-0.5" />
              <span>Prevention of further dental complications</span>
            </li>
            <li className="flex items-start gap-2">
              <Icon name="Check" size={16} className="text-success flex-shrink-0 mt-0.5" />
              <span>Improved aesthetics and confidence</span>
            </li>
            <li className="flex items-start gap-2">
              <Icon name="Check" size={16} className="text-success flex-shrink-0 mt-0.5" />
              <span>Long-term cost savings through preventive care</span>
            </li>
          </ul>
        </div>

        <div className="bg-background rounded-lg border border-border p-4">
          <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2">
            <Icon name="AlertTriangle" size={18} className="text-warning" />
            Potential Risks
          </h4>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <Icon name="AlertCircle" size={16} className="text-warning flex-shrink-0 mt-0.5" />
              <span>Temporary sensitivity or discomfort during healing</span>
            </li>
            <li className="flex items-start gap-2">
              <Icon name="AlertCircle" size={16} className="text-warning flex-shrink-0 mt-0.5" />
              <span>Rare complications including infection or nerve damage</span>
            </li>
            <li className="flex items-start gap-2">
              <Icon name="AlertCircle" size={16} className="text-warning flex-shrink-0 mt-0.5" />
              <span>Possibility of additional treatment if complications arise</span>
            </li>
            <li className="flex items-start gap-2">
              <Icon name="AlertCircle" size={16} className="text-warning flex-shrink-0 mt-0.5" />
              <span>Treatment outcomes may vary based on individual factors</span>
            </li>
          </ul>
        </div>
      </div>
      {/* Legal Notice */}
      <div className="bg-muted/50 rounded-lg border border-border p-4">
        <p className="text-xs text-muted-foreground leading-relaxed">
          <strong className="text-foreground">Legal Notice:</strong> By acknowledging these statements and providing your digital signature, you confirm that you have read and understood the proposed treatment plan, including all risks, benefits, and alternatives. You consent to the treatment as described and authorize your provider to proceed. This consent is legally binding and will be securely stored in your medical record.
        </p>
      </div>
    </div>
  );
};

export default ConsentWorkflow;